import React, { useState, useEffect } from 'react';
import api, { getGoogleDriveStorageQuota, emptyGoogleDrive, deleteOldGoogleDriveFiles } from '../../services/api';
import Loading from '../Common/Loading';

interface StorageQuota {
  limit: number;
  usage: number;
  usageInDrive: number;
  usageInTrash: number;
  limitInMB: number;
  usageInMB: number;
  usageInDriveMB: number;
  usageInTrashMB: number;
  percentUsed: number;
  lastUpdated: Date;
}

// Cache key for localStorage
const QUOTA_CACHE_KEY = 'google_drive_quota_cache';

const StorageQuotaManagement: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<StorageQuota | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [emptyingDrive, setEmptyingDrive] = useState<boolean>(false);
  const [deletingOldFiles, setDeletingOldFiles] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  // Load cached quota data from localStorage
  const loadCachedQuota = () => {
    try {
      const cachedData = localStorage.getItem(QUOTA_CACHE_KEY);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        // Convert string date back to Date object
        parsedData.lastUpdated = new Date(parsedData.lastUpdated);
        setQuota(parsedData);
        return true;
      }
    } catch (err) {
      console.error('Error loading cached quota data:', err);
    }
    return false;
  };

  // Save quota data to localStorage
  const cacheQuota = (quotaData: StorageQuota) => {
    try {
      localStorage.setItem(QUOTA_CACHE_KEY, JSON.stringify(quotaData));
    } catch (err) {
      console.error('Error caching quota data:', err);
    }
  };

  const fetchStorageQuota = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getGoogleDriveStorageQuota();
      
      if (response && response.success) {
        const { limit, usage, usageInDrive, usageInTrash } = response.quota;
        
        // Convert bytes to MB for display
        const limitInMB = Math.round(limit / (1024 * 1024));
        const usageInMB = Math.round(usage / (1024 * 1024));
        const usageInDriveMB = Math.round(usageInDrive / (1024 * 1024));
        const usageInTrashMB = Math.round(usageInTrash / (1024 * 1024));
        const percentUsed = Math.round((usage / limit) * 100);
        
        const quotaData = {
          limit,
          usage,
          usageInDrive,
          usageInTrash,
          limitInMB,
          usageInMB,
          usageInDriveMB,
          usageInTrashMB,
          percentUsed,
          lastUpdated: new Date()
        };
        
        setQuota(quotaData);
        // Cache the quota data
        cacheQuota(quotaData);
      } else {
        setError('Failed to fetch storage quota information');
      }
    } catch (err: any) {
      console.error('Error fetching Google Drive storage quota:', err);
      setError('Failed to fetch storage quota. Please try again.');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setSuccessMessage(null);
    await fetchStorageQuota();
    setRefreshing(false);
  };

  const handleEmptyDrive = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to empty Google Drive? This will permanently delete all files and cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setEmptyingDrive(true);
      setError(null);
      setSuccessMessage(null);

      const response = await emptyGoogleDrive();
      
      if (response && response.success) {
        setSuccessMessage(`Google Drive emptied successfully. Deleted ${response.filesDeleted} files/folders. Refreshing quota information...`);
        // Refresh quota after emptying drive
        await fetchStorageQuota();
      } else {
        setError('Failed to empty Google Drive. Please try again.');
      }
    } catch (err: any) {
      console.error('Error emptying Google Drive:', err);
      setError(err.response?.data?.error || 'Failed to empty Google Drive. Please try again.');
    } finally {
      setEmptyingDrive(false);
    }
  };

  const handleDeleteOldFiles = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete files older than 2 days? This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingOldFiles(true);
      setError(null);
      setSuccessMessage(null);

      // Calculate date 2 days ago
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      
      const response = await deleteOldGoogleDriveFiles(twoDaysAgo.toISOString());
      
      if (response && response.success) {
        setSuccessMessage(`Successfully deleted ${response.filesDeleted} old files. Refreshing quota information...`);
        // Refresh quota after deleting files
        await fetchStorageQuota();
      } else {
        setError('Failed to delete old files. Please try again.');
      }
    } catch (err: any) {
      console.error('Error deleting old files:', err);
      setError(err.response?.data?.error || 'Failed to delete old files. Please try again.');
    } finally {
      setDeletingOldFiles(false);
    }
  };

  useEffect(() => {
    // Try to load cached data first
    const hasCachedData = loadCachedQuota();
    
    // If no cached data, fetch from API
    if (!hasCachedData) {
      fetchStorageQuota();
    } else {
      setInitialized(true);
    }
  }, []);

  // Format the last updated time
  const formatLastUpdated = (date: Date) => {
    return date.toLocaleString();
  };

  return (
    <div className="storage-quota-management">
      <div className="section-header">
        <h2>Google Drive Storage Quota</h2>
        <div className="button-group">
          <button 
            className="refresh-button"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="delete-old-button"
            onClick={handleDeleteOldFiles}
            disabled={deletingOldFiles || loading || !quota || quota.usageInDrive === 0}
          >
            {deletingOldFiles ? 'Deleting...' : 'Delete Files > 2 Days'}
          </button>
          <button 
            className="empty-drive-button"
            onClick={handleEmptyDrive}
            disabled={emptyingDrive || loading || !quota || quota.usageInDrive === 0}
          >
            {emptyingDrive ? 'Emptying...' : 'Empty Drive'}
          </button>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : successMessage ? (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      ) : !initialized ? (
        <div className="placeholder-container">
          <div className="placeholder-content">
            <div className="placeholder-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <p>No storage quota information available.</p>
            <p>Click the Refresh button to load data.</p>
          </div>
        </div>
      ) : quota ? (
        <div className="quota-container">
          <div className="quota-summary">
            <div className="quota-card">
              <h3>Total Storage</h3>
              <p className="quota-value">{quota.limitInMB.toLocaleString()} MB</p>
            </div>
            
            <div className="quota-card">
              <h3>Used Storage</h3>
              <p className="quota-value">{quota.usageInMB.toLocaleString()} MB</p>
              <p className="quota-percentage">({quota.percentUsed}%)</p>
            </div>
            
            <div className="quota-card">
              <h3>Available Storage</h3>
              <p className="quota-value">{(quota.limitInMB - quota.usageInMB).toLocaleString()} MB</p>
            </div>
          </div>

          <div className="quota-progress">
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ 
                  width: `${quota.percentUsed}%`,
                  backgroundColor: quota.percentUsed > 90 ? '#ff4d4f' : 
                                  quota.percentUsed > 70 ? '#faad14' : '#52c41a'
                }}
              ></div>
            </div>
            <div className="progress-labels">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="quota-details">
            <h3>Storage Details</h3>
            <table className="quota-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Size (MB)</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Drive Files</td>
                  <td>{quota.usageInDriveMB.toLocaleString()}</td>
                  <td>{Math.round((quota.usageInDrive / quota.usage) * 100)}%</td>
                </tr>
                <tr>
                  <td>Trash</td>
                  <td>{quota.usageInTrashMB.toLocaleString()}</td>
                  <td>{Math.round((quota.usageInTrash / quota.usage) * 100)}%</td>
                </tr>
              </tbody>
            </table>
            
            <div className="last-updated">
              <p>Last updated: {formatLastUpdated(quota.lastUpdated)}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data">
          <p>No storage quota information available.</p>
        </div>
      )}
    </div>
  );
};

export default StorageQuotaManagement; 